import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cns from 'classnames';
import { connect } from 'react-redux';
import { Form } from 'react-bootstrap';
import Dropzone from 'react-dropzone';
import Croppie from 'croppie';
import EXIF from 'exif-js';

import Notification from '../../Notification';
import { imageUpload } from '../../../actions/Images';
import { isCloudinaryHostedImage } from '../../../utils/ImageUtils';
import VerbButton, { VERB_BUTTON_TYPE } from '../../VerbButton';
import WithTranslation from '../../../hocs/WithTranslation';
import LoadingIndicator from '../../LoadingIndicator';

window.EXIF = EXIF; // Needed for exif parsing in croppie

class ImageUpload extends Component {
    constructor(props) {
        super(props);
        this.state = {
            img: '',
            imageLoaded: false,
            imageSaved: false,
            loading: false,
            resizingImage: false
        };
    }

    static propTypes = {
        imageUpload: PropTypes.func.isRequired,
        disableClick: PropTypes.bool,
        displayLabel: PropTypes.bool,
        label: PropTypes.string,
        input: PropTypes.object.isRequired,
        cropProp: PropTypes.object,
        onImageUpload: PropTypes.func,
        className: PropTypes.string,
        intl: PropTypes.object,
        description: PropTypes.string,
        inputClassName: PropTypes.string,
        styleProps: PropTypes.object,
        buttonClassName: PropTypes.string
    };

    componentDidMount() {
        const { width = 150, height = 150, type = null, enableZoom = true } = this.props.cropProp;

        const croppieOptions = {
            // Waiting for this to be resolved https://github.com/Foliotek/Croppie/issues/309
            // Uncomment once this is fixed
            // enableExif: true,
            viewport: {
                width,
                height,
                type
            },
            boundary: {
                width: '100%',
                height: '100%'
            },
            enableZoom,
            update: (params) => {
                if (this.props.input.value) {
                    if (params.zoom === 1) {
                        this.setState({ resizingImage: false });
                    } else if (!this.state.resizingImage && params.zoom !== 1) {
                        this.setState({ resizingImage: true });
                    }
                }
            }
        };
        this.croppie = new Croppie(this.display, croppieOptions);

        if (this.props.input.value !== '') {
            const imageUrl = this._transformUrl(this.props.input.value);
            this.croppie.bind(imageUrl).then(() => {
                this.croppie.setZoom(0);
            });
        }
    }

    _transformUrl = (imageUrl) => {
        const parser = document.createElement('a');
        parser.href = imageUrl;

        let optimizedSource = imageUrl;
        if (isCloudinaryHostedImage(parser.hostname)) {
            // replaces all http with https images
            optimizedSource = optimizedSource.replace('http:', 'https:');
        }
        return optimizedSource;
    };

    _saveImage = () => {
        const reader = new FileReader();
        this.setState({
            resizingImage: false,
            loading: true
        });
        return this.croppie.result({ type: 'blob', size: 'original' }).then((blob) => {
            reader.readAsArrayBuffer(blob);
            reader.onloadend = () => {
                const file = reader.result;
                const img = {
                    file,
                    name: this.state.img.name,
                    type: this.state.img.type
                };
                this.props
                    .imageUpload(img)
                    .then((result) => {
                        this.props.input.onChange(result.url);
                        return result.url;
                    })
                    .then((resultUrl) => {
                        if (this.props.onImageUpload) {
                            const imageUrl = this._transformUrl(resultUrl);
                            this.props.onImageUpload(imageUrl, { field: this.props.input.name });
                        }
                        this.setState({ imageLoaded: true });
                        this.setState({ imageSaved: true });
                        setTimeout(() => {
                            this.setState({
                                imageLoaded: false,
                                imageSaved: false,
                                loading: false
                            });
                        }, 1000);
                    });
            };
        });
    };

    onOpenClick = () => {
        this.dropzone.open();
    };

    _makeFileUrl = (file) => URL.createObjectURL(file);

    onDrop = (files) => {
        const file = files[0];
        this.setState({
            img: {
                name: file.name,
                type: file.type
            }
        });
        this.croppie.bind(
            {
                url: this._makeFileUrl(file)
            },
            () => {
                this._saveImage();
            }
        );
    };

    render() {
        const {
            intl,
            label,
            input,
            className,
            disableClick = true,
            displayLabel = true,
            description,
            inputClassName = '',
            styleProps = {},
            buttonClassName = ''
        } = this.props;
        const { loading } = this.state;
        const { horizontal } = styleProps;
        let labelSection = null;
        if (displayLabel) {
            if (label || description) {
                labelSection = (
                    <div className={horizontal ? 'col-12 col-sm-6 no-padding' : ''}>
                        <div className={horizontal ? 'col-12 col-sm-10 no-padding' : ''}>
                            <label htmlFor={input.name} className={horizontal ? 'form-label' : ''}>
                                {label}
                            </label>
                            <p className={horizontal ? 'form-description' : ''}> {description} </p>
                        </div>
                    </div>
                );
            }
        }

        return (
            <Form.Group className={cns('form-image', className)}>
                <div>
                    <div style={{ float: 'left', height: '250px' }} />
                    {labelSection}
                    <Dropzone
                        className={cns('mx-auto', 'dropzone-input', inputClassName)}
                        ref={(node) => {
                            this.dropzone = node;
                        }}
                        onDrop={this.onDrop}
                        disableClick={disableClick}
                        accept={'image/*'}
                    >
                        {
                            (/* {isDragActive, isDragReject} */) => (
                                <img
                                    ref={(e) => {
                                        this.display = e;
                                    }}
                                />
                            )
                        }
                    </Dropzone>
                </div>
                <div className={'row'}>
                    {horizontal && <div className={'col-12 col-sm-5'} />}
                    <div className={cns('mx-auto col-12', 'text-center', buttonClassName)}>
                        <div className={'row'}>
                            <VerbButton
                                buttonType={VERB_BUTTON_TYPE.SECONDARY_DEFAULT}
                                onClick={this.onOpenClick}
                                textId={'BUTTON_CHANGE_IMAGE'}
                                defaultMessage={'Change Image'}
                            />
                            &nbsp;
                            <VerbButton
                                buttonType={VERB_BUTTON_TYPE.SECONDARY_DEFAULT}
                                disabled={!this.state.imageLoaded && !this.state.resizingImage}
                                onClick={this._saveImage}
                                textId={'PROFILE_SAVE_IMAGE'}
                                defaultMessage={'Save Image'}
                            />
                        </div>
                    </div>
                    <span className={cns('mx-auto col-12', 'text-center')}>
                        {loading ? <LoadingIndicator className={'image-crop-loading'} /> : null}
                        <Notification
                            show={this.state.imageSaved}
                            displayText={intl.formatMessage({ id: 'IMAGE_SAVED', defaultMessage: 'Image Saved!' }, {})}
                        />
                    </span>
                </div>
            </Form.Group>
        );
    }
}
const mapStateToProps = (/* state, ownProps */) => ({});

const mapDispatchToProps = (dispatch) => ({
    imageUpload: (params) => dispatch(imageUpload(params))
});
export const ImageUploadComponent = ImageUpload;
export default connect(mapStateToProps, mapDispatchToProps)(WithTranslation(ImageUpload));
